import { isNil } from 'lodash';

import type { IAppointment } from './appointment';
import { DatabaseEntities } from './base';
import { Nullable, type Maybe } from './globals';
import { LoadTypeDirection } from './loadtype';
import { NovaDate, type DateLike } from './nova-date';

export enum UnitType {
  Appointment = 'Appointment',
  Units = 'Units'
}

export enum UnitGrouper {
  LoadType = DatabaseEntities.LoadType,
  Dock = DatabaseEntities.Dock,
  Warehouse = DatabaseEntities.Warehouse,
  LoadTypeGroup = DatabaseEntities.LoadTypeGroup
}

/**
 * Constants for converting Luxon weekday values (1-7, where 1 = Monday, 7 = Sunday)
 * to array indices (0-6, where 0 = Sunday, 6 = Saturday)
 */
export const LUXON_WEEKDAY_SUNDAY = 7;
export const ARRAY_INDEX_SUNDAY = 0;

/**
 * Converts Luxon weekday (1-7, Mon-Sun) to array index (0-6, Sun-Sat)
 * @param luxonWeekday - Luxon weekday value (1 = Monday, 7 = Sunday)
 * @returns Array index (0 = Sunday, 6 = Saturday)
 */
export function luxonWeekdayToArrayIndex(luxonWeekday: number): number {
  return luxonWeekday === LUXON_WEEKDAY_SUNDAY ? ARRAY_INDEX_SUNDAY : luxonWeekday;
}

/**
 * Calendar date (yyyy-MM-dd) for unit-limit counts: `appointmentStart` interpreted in `timezone`.
 * @throws Error if the start instant cannot be parsed or the zoned result is invalid.
 */
export function getCountedDate(appointmentStart: DateLike, timezone: string): string {
  try {
    return new NovaDate(appointmentStart).setTz(timezone).ensureValid().toISODateString();
  } catch (error) {
    throw new Error(`Invalid appointment start date: ${error}`, { cause: error });
  }
}

/**
 * Counted-date range for unit limits: `start` / `end` instants mapped to `yyyy-MM-dd` in `timezone`
 * (same rules as {@link getCountedDate} for each bound).
 * @throws Error if either bound cannot be parsed or the zoned result is invalid.
 */
export function getCountedDateRange(
  input: { start: DateLike; end: DateLike },
  timezone: string
): { start: string; end: string } {
  return {
    start: getCountedDate(input.start, timezone),
    end: getCountedDate(input.end, timezone)
  };
}

/**
 * True when counted day (warehouse TZ), dock, or load type no longer align with the prior
 * appointment — the update should follow the full recount path for unit limits.
 */
export function isEligibleForRecount(
  appointment: IAppointment,
  dto: IAppointment,
  timezone: string
): boolean {
  const sameDock = isNil(dto.dockId) || dto.dockId === appointment.dockId;
  const sameLoadType = isNil(dto.loadTypeId) || dto.loadTypeId === appointment.loadTypeId;
  const start = isNil(dto.start) ? appointment.start : dto.start;
  const sameCountedDay =
    getCountedDate(start, timezone) === getCountedDate(appointment.start, timezone);
  return !sameCountedDay || !sameDock || !sameLoadType;
}

/** True when unit-limit rules apply: Inbound or InOut load types only. */
export function isLoadTypeEligible(loadType: Maybe<{ direction: LoadTypeDirection }>): boolean {
  if (!loadType) {
    return false;
  }
  return (
    loadType.direction === LoadTypeDirection.Inbound ||
    loadType.direction === LoadTypeDirection.InOut
  );
}

export interface IUnitLimit {
  loadTypes: string[] | null | undefined;

  // general liimits
  dailyLimit: Nullable<number>;
  weeklyLimit: Nullable<number>;
  monthlyLimit: Nullable<number>;

  // specific weekdays limits
  mondayLimit: Nullable<number>;
  tuesdayLimit: Nullable<number>;
  wednesdayLimit: Nullable<number>;
  thursdayLimit: Nullable<number>;
  fridayLimit: Nullable<number>;
  saturdayLimit: Nullable<number>;
  sundayLimit: Nullable<number>;

  // specific months limits
  januaryLimit: Nullable<number>;
  februaryLimit: Nullable<number>;
  marchLimit: Nullable<number>;
  aprilLimit: Nullable<number>;
  mayLimit: Nullable<number>;
  juneLimit: Nullable<number>;
  julyLimit: Nullable<number>;
  augustLimit: Nullable<number>;
  septemberLimit: Nullable<number>;
  octoberLimit: Nullable<number>;
  novemberLimit: Nullable<number>;
  decemberLimit: Nullable<number>;
}
